import AttributionModelsIcon from '@precis-digital/kurama/icons/AttributionModelsIcon'
import BudgetOptimiserIcon from '@precis-digital/kurama/icons/BudgetOptimiserIcon'
import ChannelGroupingsIcon from '@precis-digital/kurama/icons/ChannelGroupingsIcon'
import ConnectDataSourcesIcon from '@precis-digital/kurama/icons/ConnectDataSourcesIcon'
import CopilotIcon from '@precis-digital/kurama/icons/CopilotIcon'
import CreativeAssetsIcon from '@precis-digital/kurama/icons/CreativeAssetsIcon'
import CreativeReportingIcon from '@precis-digital/kurama/icons/CreativeReportingIcon'
import DataInsightsIcon from '@precis-digital/kurama/icons/DataInsightsIcon'
import EnhancedSignalsIcon from '@precis-digital/kurama/icons/EnhancedSignalsIcon'
import PlugIcon from '@precis-digital/kurama/icons/PlugIcon'
import PresentationBoardIcon from '@precis-digital/kurama/icons/PresentationBoardIcon'
import ProjectsIcon from '@precis-digital/kurama/icons/ProjectsIcon'
import RecommendationsIcon from '@precis-digital/kurama/icons/RecommendationsIcon'
import ShopIcon from '@precis-digital/kurama/icons/ShopIcon'
import { createElement, type ReactElement } from 'react'
import { APP_ROUTES } from 'shared/routes'
import type { CopilotNavigationTarget } from '../pageContext/types'
import { formatCopilotIdentifierLabel } from 'shared/copilot/components/ToolCall/pageElementDisplay'

/**
 * Allowlisted in-app routes for `change_url` / navigation detail tools.
 *
 * `description` is the short summary exposed in tool catalogs (e.g. change_url).
 * `extendedDescription` is multi-line product detail — only returned via
 * `get_navigation_target_details`, never stuffed into default agent context.
 */
export const COPILOT_NAVIGATION_TARGETS: CopilotNavigationTarget[] = [
  {
    id: 'home',
    label: 'Home',
    description:
      'Daily performance dashboard for connected digital channels: scorecard metrics, progress-over-time charts, a detailed report table, market/channel filters, date range, and recommendation cards. Use for a quick cross-channel overview and to jump into suggested improvements.',
    extendedDescription: `
The Home page is Alvie’s daily performance dashboard for connected digital channels.

What the user sees and can do:
- Performance metrics / scorecards: key KPIs for the selected report, date range, and filters (user can choose which metrics appear).
- Progress over time: charts with local “Show me” / comparison controls.
- Detailed report table: structured channel/market breakdowns with configurable columns.
- Recommendations strip: actionable cards on the right (same feed as the Recommendations page).
- Filters: market and channel filters that reshape the whole dashboard.
- Date range and comparison period via the calendar control.
- Conversion event selection and report switching (when multiple reporting solutions feed Home).
- Setup path: needs connected data sources plus a Reporting solution configured for Home.

Navigate here when the user wants a cross-channel daily overview, to tweak Home filters/metrics/dates, or to scan recommendations alongside performance.
`.trim(),
    path: APP_ROUTES.home.basePage,
    icon: ShopIcon
  },
  {
    id: 'recommendations',
    label: 'Recommendations',
    description:
      'Actionable optimisation, data-quality, and best-practice recommendations derived from connected accounts (e.g. underperforming Google Ads campaigns). Open cards for detail, context on what to change, and follow-up actions.',
    extendedDescription: `
The Recommendations page lists actionable suggestions Alvie generates from connected data sources.

What the user sees and can do:
- Cards for optimisation (e.g. underperforming Google Ads campaigns), data quality, and best-practice guidance.
- Open a recommendation for detail: where the issue sits in the channel, why Alvie suggests a change, and related context.
- Change severity, assignee, and status on list rows (including ignore until a date).
- Use the more menu to edit configuration, open How to fix, or open View Advanced.
- Recommendations also appear on Home; this page is the dedicated full list and detail view.

Navigate here when the user asks what to improve, wants campaign-level suggested actions, or needs to review the recommendation backlog beyond the Home sidebar.
`.trim(),
    path: APP_ROUTES.recommendations.basePage,
    icon: RecommendationsIcon
  },
  {
    id: 'reporting_solutions',
    label: 'Reporting solutions',
    description:
      'Create and manage ready-made marketing reports that pull from connected sources into BigQuery and Data Studio (e.g. Starter Report, channel grouping performance, creative insights). Use to set up, edit, activate, or open report dashboards.',
    extendedDescription: `
Reporting solutions are Alvie’s managed report products: they fetch from connected sources, land data in BigQuery, transform it, and publish Data Studio dashboards.

What the user sees and can do:
- Overview of report configs (Starter Report, channel grouping performance, creative insights, Meta visual creative, and other available reports).
- Create, edit, copy, activate, pause, or delete report configurations.
- Choose data sources / accounts, custom groupings, destinations, and Data Studio outputs during setup.
- Open dashboards and check run/status for each report.
- Home can be driven by one of these reports (switch report from Home when multiple exist).

Navigate here to set up or manage reporting pipelines and dashboards—not for ad-hoc GA4 exploration (use Alvie AI Pro) and not for raw connector jobs (use Connectors).
`.trim(),
    path: APP_ROUTES.reportingSolutions.basePage,
    icon: PresentationBoardIcon
  },
  {
    id: 'connectors',
    label: 'Connectors',
    description:
      'Scheduled data pipelines that sync marketing platforms (Google Ads, Meta, GA4, TikTok, LinkedIn, and more) into BigQuery. Manage connector health, credentials, run-now, status, and destination tables that power Alvie models and reports.',
    extendedDescription: `
Connectors are scheduled integrations that sync marketing platform data into BigQuery with Alvie-managed quality, retries, and credential handling.

What the user sees and can do:
- List of connectors with type, data source, last edited, and health/status.
- Create/edit connectors for platforms such as Google Ads, Meta, GA4, Search Console, TikTok, LinkedIn, DV360, Merchant Center, Sheets, and others.
- Activate, run immediately, view detailed status, open BigQuery tables, reconnect credentials, copy, or delete.
- Data lands in the data lake for reuse across Attribution, Budget Optimiser, Reporting solutions, and more.
- Failures notify the team; jobs retry on transient upstream API errors.

Navigate here for pipeline/sync health, credential refresh, or “is my Meta/Google data landing in BigQuery?”—not for linking accounts into the workspace (use Data sources).
`.trim(),
    path: APP_ROUTES.connectors.basePage,
    icon: PlugIcon
  },
  {
    id: 'attribution_models',
    label: 'Attribution models',
    description:
      'Cross-channel attribution configurations and dashboards that credit conversions across the customer journey (Alvie regression-based and market models). Use to create/activate models, review status, and analyse unbiased channel contribution vs platform last-click bias.',
    extendedDescription: `
Attribution models measure which channels and touchpoints drive conversions across the full journey, reducing last-click / platform bias.

What the user sees and can do:
- Overview of model configs: name, status, last edited; filter/sort and open dashboards.
- Create models: name, GA4 + spend/conversion sources (including custom spend/conversions from Sheets/BigQuery), GCP/BigQuery destination, channel grouping, revenue/currency settings, then activate.
- Alvie regression-based models and market attribution models are supported; creating an Alvie attribution model can auto-create a linked Budget Optimiser config.
- Dashboards for analysis; activate/pause/delete via the more menu; inspect detailed status.
- Custom spend lets unsupported platforms (affiliates, sponsorships, etc.) enter the model with a fixed schema.

Navigate here for cross-channel credit, “which channel deserves budget?”, or attribution setup—not for day-to-day KPI glance (Home) or spend reallocation UI (Budget Optimiser).
`.trim(),
    path: APP_ROUTES.attributionModels.basePage,
    icon: AttributionModelsIcon
  },
  {
    id: 'data_sources',
    label: 'Data sources',
    description:
      'Connected platform accounts and credentials used across Alvie (Connected / Disconnected / Error). Connect new accounts, reconnect expired credentials, filter by type/status, and check which sources feed models, reports, and recommendations.',
    extendedDescription: `
Data sources are the connected platform accounts and credentials that unlock Alvie features.

What the user sees and can do:
- Overview of all connected accounts with status Connected / Disconnected / Error, credentials in use, and type filters.
- Connect new sources: pick platform, auth, select accounts.
- Reconnect (“Sign in to reconnect”) when credentials expire; banners call out disconnected sources.
- Delete accounts carefully—other tools may depend on them and can pause if a source breaks.
- Required before Attribution, Budget Optimiser, Reporting solutions, Connectors, Recommendations, etc. can use that account’s data.

Navigate here to add/reconnect accounts or diagnose auth/status—not to configure sync schedules (Connectors) or report definitions (Reporting solutions).
`.trim(),
    path: APP_ROUTES.dataSources.basePage,
    icon: ConnectDataSourcesIcon
  },
  {
    id: 'budget_optimiser',
    label: 'Budget Optimiser',
    description:
      'Budget allocation models that use historical spend/performance and an attribution model to recommend how to redistribute ad spend for higher ROI. Configure scenarios (optimise for revenue/conversions, spend targets, redistribution limits) and review channel/campaign recommendations.',
    extendedDescription: `
Budget Optimiser recommends how to redistribute marketing spend for higher return, using historical performance and an attribution (or market) model.

What the user sees and can do:
- Overview of Budget Optimiser configs/models and their dashboards.
- Setup: name/type (single platform, multiple platforms, GA4 attribution), accounts, BigQuery destination, channel grouping, activate.
- Analysis scenarios: optimise for revenue vs conversions; set ad spend targets; limit redistribution within channels, within data sources, or with no limit.
- Review recommended budget shifts by channel/campaign and refresh scenarios after changing settings.
- Often paired with Attribution models; Alvie attribution configs can spawn Budget Optimiser configs automatically.

Navigate here for “how should we reallocate budget?” and scenario modelling—not for raw attribution methodology setup (Attribution models) or creative performance (Creative Reporting).
`.trim(),
    path: APP_ROUTES.budgetOptimiser.basePage,
    icon: BudgetOptimiserIcon
  },
  {
    id: 'bidding_signals',
    label: 'Bidding signals',
    description:
      'Enrich and transform website/app conversion events (e.g. profit or lookup-value transforms) before sending them to ad platforms such as GA4, Google Ads, Meta, or server-side GTM—improving bidding signals with less personal data. Manage configs and event dashboards here.',
    extendedDescription: `
Bidding Signals (also referred to as Enhanced Signals in places) enriches conversion events before they reach ad platforms, improving bidding without shipping excess personal data.

What the user sees and can do:
- Overview of configs: name/id, last edited, status; edit, copy, activate, open dashboards.
- Setup: choose a transformation (profit, event lookup value, item lookup value), then targets (GA4 gtag/Firebase, server-side GTM, Google Ads click/restate conversions, Meta CAPI).
- Dashboard: events received/transformed, adjusted values vs revenue, events over time, detailed event table with market filters and event-id search.
- Designed for cookie-minimised, controlled signal quality into downstream bidding systems.

Navigate here to configure or debug conversion enrichment pipelines into ads platforms—not for GA4 reporting analysis (Alvie AI Pro / Reporting solutions).
`.trim(),
    path: APP_ROUTES.enhancedSignals.basePage,
    icon: EnhancedSignalsIcon
  },
  {
    id: 'custom_groupings',
    label: 'Custom groupings',
    description:
      'Reusable channel and market (and related) groupings that bucket cross-platform campaign data into consistent logical categories. Required input for Attribution models and Budget Optimiser so spend and conversions map cleanly across platforms.',
    extendedDescription: `
Custom groupings categorise cross-platform campaign data into shared logical buckets (especially channel and market groupings).

What the user sees and can do:
- Create and edit groupings used so Attribution models and Budget Optimiser understand how to allocate conversions and budget across platforms without relying only on native tagging.
- Channel grouping and market grouping are the primary types; keep the set small unless a real second taxonomy is needed.
- Also used by several Reporting solutions / creative reports for consistent market/channel cuts and optional extra groupings (audience, format, funnel, etc. depending on report).
- Purpose: one consistent taxonomy across Google, Meta, and other sources.

Navigate here when setting up or fixing channel/market taxonomies that models and reports depend on.
`.trim(),
    path: APP_ROUTES.customGroupings.basePage,
    icon: ChannelGroupingsIcon
  },
  {
    id: 'creative_reporting',
    label: 'Creative Reporting',
    description:
      'Visual-first creative performance reporting (Meta and TikTok today): see images/videos next to metrics, spot top concepts, ad fatigue, format comparison, and video deep-dives at ad/creative level rather than campaign-only views.',
    extendedDescription: `
Creative Reporting is visual-first performance analysis of ads/creatives (Meta and TikTok today; more platforms planned).

What the user sees and can do:
- Unite creative assets (images/videos) with metrics (clicks, conversions, CPA, etc.) in one view.
- Dashboards such as top-performing concepts, ad fatigue, format comparison, video deep dive, and custom boards.
- Work at ad/creative granularity—not only campaign or ad-set rollups.
- Set up/manage Creative Reporting configs and open dashboards from the feature area.
- Distinct from Creative Assets (production/collaboration) and from general channel KPIs on Home.

Navigate here for creative strategy, fatigue, and format/concept winners—not for uploading/reviewing assets (Creative Assets).
`.trim(),
    path: APP_ROUTES.creativeReporting.basePage,
    icon: CreativeReportingIcon
  },
  {
    id: 'creative_assets',
    label: 'Creative Assets',
    description:
      'Creative production workspace: projects, ad library, asset upload/versioning, review/request-changes workflows, messaging, and brand portal—for managing creatives and collaboration, not performance analytics (use Creative Reporting for that).',
    extendedDescription: `
Creative Assets is the production and collaboration workspace for creatives—not a performance dashboard.

What the user sees and can do:
- Projects with overview, ad library, brand portal, and member collaboration.
- Upload assets, version history, download, and organise creatives in a library.
- Request changes / review workflows, messaging on assets, and mark requests resolved.
- Private projects and resource management for creative ops between brands and teams.

Navigate here to manage files, reviews, and creative production logistics. For performance of those creatives in ads, use Creative Reporting (or Reporting solutions creative insights).
`.trim(),
    path: APP_ROUTES.creativeAssets.basePage,
    icon: CreativeAssetsIcon
  },
  {
    id: 'workspace_members',
    label: 'Workspace members',
    description:
      'Workspace members tab: list members, filter by role, invite teammates, and manage pending invites for the current Alvie client.',
    extendedDescription: `
The Workspace members tab manages who has access to the current Alvie client.

What the user sees and can do:
- View members with Viewer / Editor / Admin roles.
- Filter the members table by role.
- Invite teammates by email (Editors and Admins; only Admins can invite as Admin).
- Resend or revoke pending invitations.
- Change member roles subject to permission and sole-admin rules.

Navigate here for access control and invitations—not for notification email preferences (use Workspace notification settings) or connecting ad platforms (Data sources).
`.trim(),
    path: APP_ROUTES.workspace.membersPage,
    icon: ProjectsIcon
  },
  {
    id: 'workspace_notification_settings',
    label: 'Workspace notification settings',
    description:
      'Workspace notification settings tab: configure the current user’s email preferences for job statuses, recommendations, and disconnected data sources.',
    extendedDescription: `
The Workspace notification settings tab controls email notifications for the signed-in user in the current client.

What the user sees and can do:
- Toggle job status emails overall and per product (Reporting Solutions, Connectors, Attribution Models, Budget optimiser, Bidding signals).
- Toggle recommendation emails and choose severity levels (critical, moderate, low).
- Toggle disconnected data source emails.
- Save changes to persist preferences.

Navigate here for email notification preferences—not for inviting members or changing roles (use Workspace members).
`.trim(),
    path: APP_ROUTES.workspace.notificationSettingsPage,
    icon: ProjectsIcon
  },
  {
    id: 'copilot_settings',
    label: 'Alvie AI settings',
    description:
      'Alvie AI settings page: manage shared workspace custom instructions and knowledge sources for Alvie AI in this client. Workspace admins edit General settings; editors and admins manage Knowledge sources. Tabs: General, Knowledge sources, Skills, MCP, Usage.',
    extendedDescription: `
The Alvie AI settings page configures Alvie AI for the current client.

What the user sees and can do:
- General — read workspace-level Alvie AI custom instructions (tone, format, team preferences). Workspace admins can add or edit them.
- Knowledge sources — create and manage document sources (Drive, Help Center, Teamwork, uploads) that Alvie AI can use in chat. Editors and admins can edit; viewers can browse.
- Skills, MCP, and Usage — reserved for upcoming configuration (coming soon).
- Save General changes (admins only; optimistic concurrency — reload if someone else saved first).

Navigate here for shared Alvie AI behaviour—not for personal instructions (use Profile → Preferences) or members/invites (Workspace members).
`.trim(),
    path: APP_ROUTES.copilot.settingsGeneralPage,
    icon: CopilotIcon
  },
  {
    id: 'copilot_pro',
    label: 'Alvie AI Pro',
    description:
      'Full-page analytics Alvie AI for deep GA4 and Google Search Console work: custom reports, forecasting, causal impact, and anomaly detection with a selected data source. Prefer this over the sidenav Alvie AI when the user needs analytical depth beyond page tools or help search.',
    extendedDescription: `
Alvie AI Pro is the full-page analytics assistant (separate from the sidenav Alvie AI).

What the user sees and can do:
- Select a connected GA4 or Google Search Console data source and ask deep analytical questions.
- Capabilities include custom reports, exploration of events/fields, forecasting, causal impact, and anomaly detection (see get_copilot_pro_capabilities for the live list).
- Datasource picker and attachment actions that the drawer agent does not offer.
- Path is /ai/chat; navigation via change_url with targetId copilot_pro requires user Approve.

Navigate here when the user needs deep analytics beyond page tools and help-center search. Do not claim the sidenav Alvie AI can run those analyses itself.
`.trim(),
    path: APP_ROUTES.copilot.chatPage,
    icon: CopilotIcon
  }
]

/**
 * Looks up an allowlisted in-app navigation target by route id.
 * Returns undefined when the id is not part of `COPILOT_NAVIGATION_TARGETS`.
 */
export const findCopilotNavigationTarget = (targetId: string): CopilotNavigationTarget | undefined =>
  COPILOT_NAVIGATION_TARGETS.find((target) => target.id === targetId)

/**
 * Resolves label and icon for a navigation target id used in tool-call cards.
 * Falls back to a formatted id label and default icon when the target is unknown.
 */
export const getCopilotNavigationTargetDisplay = (
  targetId: string | undefined
): { label: string; icon: ReactElement } => {
  const target = targetId == null ? undefined : findCopilotNavigationTarget(targetId)

  if (target != null) {
    return { label: target.label, icon: createElement(target.icon) }
  }

  return {
    label: targetId == null ? 'Page' : formatCopilotIdentifierLabel(targetId),
    icon: createElement(DataInsightsIcon)
  }
}
